import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Box,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  useTheme,
  alpha,
} from '@mui/material';
import {
  DarkMode,
  LightMode,
  Logout,
  Notifications,
  Menu as MenuIcon,
  CameraAlt,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutUser } from '../store/storeSlice';
import { toggleDarkMode } from '../store/settingsSlice';
import WebSocketNavbarStatus from './WebSocketNavbarStatus';

const Navbar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isDarkMode = useSelector((state) => state.settings.darkMode);
  const user = useSelector((state) => state.store.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMobileMenuAnchor(null);
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
    handleMenuClose();
  };

  const handleThemeToggle = () => {
    dispatch(toggleDarkMode());
  };

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMenuAnchor);

  return (
    <AppBar 
      position="static" 
      elevation={0}
      sx={{
        background: isDarkMode 
          ? 'linear-gradient(135deg, #0d1117 0%, #161b22 30%, #21262d 70%, #0d1117 100%)'
          : 'linear-gradient(135deg, #f6f8fa 0%, #ffffff 30%, #f6f8fa 70%, #f6f8fa 100%)',
        borderBottom: `1px solid ${isDarkMode ? '#21262d' : '#d0d7de'}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      <Toolbar 
        sx={{ 
          minHeight: { xs: '38px !important', md: '48px !important' },
          px: { xs: 1, md: 3 },
          py: { xs: 0, md: 0 },
          justifyContent: 'space-between',
        }}
      >
        {/* Left Section - Logo & Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, flex: { xs: 1, md: 'none' }, minWidth: 0 }}>
          <Avatar
            sx={{
              width: { xs: 28, md: 32 },
              height: { xs: 28, md: 32 },
              backgroundColor: isDarkMode ? '#58a6ff' : '#0969da',
              color: 'white',
              flexShrink: 0,
            }}
          >
            <CameraAlt sx={{ fontSize: { xs: '18px', md: '20px' } }} />
          </Avatar>
          
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '14px', md: '18px' },
              color: isDarkMode ? '#f0f6fc' : '#24292f',
              display: { xs: 'none', md: 'block' },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Система мониторинга
          </Typography>
        </Box>

        {/* Center Section - WebSocket Status */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          flex: 1,
          justifyContent: 'center',
          mx: 2
        }}>
          <WebSocketNavbarStatus />
        </Box>

        {/* Right Section - Actions */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, md: 1 },
          flexShrink: 0
        }}>
          {/* Theme Toggle */}
          <Tooltip title={isDarkMode ? 'Переключить на светлую тему' : 'Переключить на темную тему'}>
            <IconButton
              onClick={handleThemeToggle}
              size="small"
              sx={{
                color: isDarkMode ? '#f0f6fc' : '#24292f',
                padding: { xs: '6px', md: '8px' },
                '&:hover': {
                  backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                },
              }}
            >
              {isDarkMode ? <LightMode sx={{ fontSize: { xs: '18px', md: '20px' } }} /> : <DarkMode sx={{ fontSize: { xs: '18px', md: '20px' } }} />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Уведомления">
            <IconButton
              size="small"
              sx={{
                color: isDarkMode ? '#f0f6fc' : '#24292f',
                padding: { xs: '6px', md: '8px' },
                display: { xs: 'none', md: 'flex' },
                '&:hover': {
                  backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                },
              }}
            >
              <Badge badgeContent={0} color="error" variant="dot">
                <Notifications sx={{ fontSize: { xs: '18px', md: '20px' } }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Profile */}
          <Tooltip title="Настройки аккаунта">
            <IconButton
              onClick={handleProfileMenuOpen}
              size="small"
              sx={{
                p: { xs: 0.25, md: 0.5 },
                display: { xs: 'none', md: 'flex' },
                '&:hover': {
                  backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                },
              }}
            >
              <Avatar
                sx={{
                  width: { xs: 24, md: 28 },
                  height: { xs: 24, md: 28 },
                  backgroundColor: isDarkMode ? '#1f6feb' : '#0550ae',
                  fontSize: { xs: '11px', md: '12px' },
                  fontWeight: 600,
                }}
              >
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* Mobile Menu */}
          <Tooltip title="Меню">
            <IconButton
              onClick={handleMobileMenuOpen}
              size="small"
              sx={{
                color: isDarkMode ? '#f0f6fc' : '#24292f',
                display: { xs: 'flex', md: 'none' },
                padding: { xs: '6px', md: '8px' },
                '&:hover': {
                  backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                },
              }}
            >
              <MenuIcon sx={{ fontSize: { xs: '20px', md: '24px' } }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Desktop Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: { xs: 240, md: 280 },
              backgroundColor: isDarkMode ? '#161b22' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#21262d' : '#d0d7de'}`,
              boxShadow: isDarkMode 
                ? '0 8px 24px rgba(0, 0, 0, 0.4)' 
                : '0 8px 24px rgba(0, 0, 0, 0.15)',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* User Info */}
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.5 } }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                color: isDarkMode ? '#f0f6fc' : '#24292f',
                fontSize: { xs: '13px', md: '14px' }
              }}
            >
              {user?.name || 'Пользователь'}
            </Typography>
            {user?.roles && user.roles.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {user.roles.map((role, index) => (
                  <Chip
                    key={index}
                    label={role}
                    size="small"
                    sx={{
                      height: { xs: 18, md: 20 },
                      fontSize: { xs: '10px', md: '11px' },
                      backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                      color: isDarkMode ? '#58a6ff' : '#0969da',
                      border: `1px solid ${alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.2)}`,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: isDarkMode ? '#21262d' : '#d0d7de' }} />

          {/* Logout */}
          <MenuItem
            onClick={handleLogout}
            sx={{
              color: '#f85149',
              py: { xs: 1, md: 1.5 },
              '&:hover': {
                backgroundColor: alpha('#f85149', 0.1),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>
              <Logout sx={{ fontSize: { xs: '18px', md: '20px' }, color: '#f85149' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Выйти" 
              primaryTypographyProps={{ 
                fontSize: { xs: '13px', md: '14px' } 
              }} 
            />
          </MenuItem>
        </Menu>

        {/* Mobile Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={isMobileMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: { xs: 240, md: 280 },
              maxWidth: { xs: '90vw', md: 'none' },
              backgroundColor: isDarkMode ? '#161b22' : '#ffffff',
              border: `1px solid ${isDarkMode ? '#21262d' : '#d0d7de'}`,
              boxShadow: isDarkMode 
                ? '0 8px 24px rgba(0, 0, 0, 0.4)' 
                : '0 8px 24px rgba(0, 0, 0, 0.15)',
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* User Info in Mobile Menu */}
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.5 } }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 600, 
                color: isDarkMode ? '#f0f6fc' : '#24292f',
                fontSize: { xs: '13px', md: '14px' },
                mb: 1
              }}
            >
              {user?.name || 'Пользователь'}
            </Typography>
            {user?.roles && user.roles.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {user.roles.map((role, index) => (
                  <Chip
                    key={index}
                    label={role}
                    size="small"
                    sx={{
                      height: { xs: 18, md: 20 },
                      fontSize: { xs: '10px', md: '11px' },
                      backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                      color: isDarkMode ? '#58a6ff' : '#0969da',
                      border: `1px solid ${alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.2)}`,
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
          
          <Divider sx={{ borderColor: isDarkMode ? '#21262d' : '#d0d7de' }} />
          
          <Box sx={{ px: { xs: 1.5, md: 2 }, py: { xs: 1, md: 1.5 } }}>
            <WebSocketNavbarStatus />
          </Box>
          
          <Divider sx={{ borderColor: isDarkMode ? '#21262d' : '#d0d7de' }} />
          
          <MenuItem
            onClick={handleLogout}
            sx={{
              color: '#f85149',
              py: { xs: 1, md: 1.5 },
              '&:hover': {
                backgroundColor: alpha('#f85149', 0.1),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: { xs: 36, md: 40 } }}>
              <Logout sx={{ fontSize: { xs: '18px', md: '20px' }, color: '#f85149' }} />
            </ListItemIcon>
            <ListItemText 
              primary="Выйти" 
              primaryTypographyProps={{ 
                fontSize: { xs: '13px', md: '14px' } 
              }} 
            />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;